/**
 * Adapters canônicos entre estado do editor e ProjectMetadataV1.
 * Fonte: integration-plan/context/metadata-contract.md
 */

import {
  emptyMetadataV1,
  METADATA_SCHEMA_VERSION,
  type MetadataColumn,
  type MetadataGroup,
  type MetadataRelationship,
  type MetadataSettings,
  type MetadataTable,
  type ProjectMetadataV1,
} from './schema'
import type { Column, ColumnDefaults, DatabaseDialect, DiagramVersionSnapshot, Groups, Relation, Table } from '@/types'
import type { LineMode } from '@/stores/settingsStore'
import { newUid } from '@/utils/uid'
import { migrateDiagramIdentity } from '@/services/identityMigration'

// ── Tipos parciais de estado ──────────────────────────────────────────────────

export interface DiagramStateSlice {
  tables: Table[]
  rels: Relation[]
  groups: Groups
  defaults: ColumnDefaults
  projectName: string
  dialect: DatabaseDialect
}

export interface ViewportStateSlice {
  sc: number
  tx: number
  ty: number
}

export interface TabViewStateSlice {
  hiddenTableIds: string[]
}

export interface SettingsStateSlice {
  defaultLineMode: LineMode
  relationshipAnimationEnabled: boolean
  showGrid: boolean
  themePreference: 'system' | 'light' | 'dark'
  sbCollapsed: Record<string, boolean>
  defaults: ColumnDefaults
}

export interface EditorStateInput {
  diagram: DiagramStateSlice
  viewport: ViewportStateSlice
  tabView: TabViewStateSlice
  settings: SettingsStateSlice
  project: { name: string; description: string; dialect: DatabaseDialect }
}

// ── Column helpers ────────────────────────────────────────────────────────────

function columnToMeta(col: Column): MetadataColumn {
  return {
    id: col.id,
    key: col.key,
    pk: col.pk,
    fk: col.fk,
    uq: col.uq,
    name: col.name,
    type: col.type,
    logicalType: col.logicalType,
    notNull: col.notNull,
    blank: col.blank,
    desc: col.desc,
    defaultValue: col.defaultValue,
    checkExpression: col.checkExpression,
  }
}

export function metaToColumn(col: MetadataColumn): Column {
  return {
    id: col.id || newUid(),
    key: col.key,
    pk: col.pk ?? false,
    fk: col.fk ?? false,
    uq: col.uq ?? false,
    name: col.name,
    type: col.type,
    logicalType: col.logicalType,
    notNull: col.notNull,
    blank: col.blank,
    desc: col.desc,
    defaultValue: col.defaultValue ?? '',
    checkExpression: col.checkExpression ?? '',
  }
}

// ── editorStateToMetadata ─────────────────────────────────────────────────────

export function editorStateToMetadata(
  state: EditorStateInput,
  opts?: { forCommit?: boolean },
): ProjectMetadataV1 {
  const { diagram, viewport, tabView, settings, project } = state
  const forCommit = opts?.forCommit ?? false

  // Tables: strip x/y, put positions in layout.positions
  const positions: Record<string, { x: number; y: number }> = {}
  const tables: MetadataTable[] = diagram.tables.map((t) => {
    positions[t.id] = { x: t.x, y: t.y }
    return {
      id: t.id,
      schema: t.schema,
      name: t.name,
      fullName: t.fullName,
      comment: t.comment,
      g: t.g,
      width: t.width,
      cols: t.cols.map(columnToMeta),
      indexes: t.indexes ?? [],
    }
  })

  // Groups: Record → Array
  const groups: MetadataGroup[] = Object.values(diagram.groups)

  // Relationships
  const relationships: MetadataRelationship[] = diagram.rels.map((r) => ({
    id: r.id,
    from: r.from,
    to: r.to,
    col: r.col,
    type: r.type,
    fromCardinality: r.fromCardinality,
    toCardinality: r.toCardinality,
    mid: r.mid,
    fromAnchor: r.fromAnchor,
    toAnchor: r.toAnchor,
    waypoints: r.waypoints ?? [],
    isManual: r.isManual,
    labelT: r.labelT,
    constraintName: r.constraintName,
    relationshipKey: r.relationshipKey,
  }))

  // Settings defaults: use diagram.defaults (has full Column[] objects)
  const metaDefaults = {
    prefix: diagram.defaults.prefix.map(columnToMeta),
    suffix: diagram.defaults.suffix.map(columnToMeta),
  }

  const metaSettings: MetadataSettings = {
    defaults: metaDefaults,
    defaultLineMode: settings.defaultLineMode as MetadataSettings['defaultLineMode'],
    relAnimation: settings.relationshipAnimationEnabled,
    grid: settings.showGrid,
  }

  if (!forCommit) {
    metaSettings.userView = {
      hiddenTableIds: tabView.hiddenTableIds,
      sidebarCollapsed: Object.values(settings.sbCollapsed).some(Boolean),
      theme: settings.themePreference,
    }
  }

  return {
    schemaVersion: METADATA_SCHEMA_VERSION,
    project: {
      name: project.name || diagram.projectName,
      description: project.description,
      dialect: project.dialect || diagram.dialect,
    },
    tables,
    relationships,
    groups,
    layout: {
      positions,
      zoom: viewport.sc,
      pan: { x: viewport.tx, y: viewport.ty },
    },
    settings: metaSettings,
  }
}

// ── metadataToEditorState ─────────────────────────────────────────────────────

export interface EditorStateOutput {
  diagram: DiagramStateSlice
  viewport: ViewportStateSlice
  tabView: TabViewStateSlice
  project: { name: string; description: string; dialect: DatabaseDialect }
}

export function metadataToEditorState(metadata: ProjectMetadataV1): EditorStateOutput {
  // Migração de identidade só para histórico legado (schema < 1.1.0:
  // id=fullName, colunas sem id). Dados já em 1.1.0 passam inalterados —
  // seus ids (UUID) já são a identidade canônica.
  const isLegacy = (metadata.schemaVersion ?? '1.0.0') < METADATA_SCHEMA_VERSION
  const migrated = isLegacy
    ? migrateDiagramIdentity({
        tables: metadata.tables ?? [],
        rels: metadata.relationships ?? [],
        positions: metadata.layout?.positions ?? {},
        hiddenTableIds: metadata.settings?.userView?.hiddenTableIds ?? [],
      })
    : {
        tables: (metadata.tables ?? []) as unknown as Record<string, unknown>[],
        rels: (metadata.relationships ?? []) as unknown as Record<string, unknown>[],
        positions: (metadata.layout?.positions ?? {}) as Record<string, unknown>,
        hiddenTableIds: metadata.settings?.userView?.hiddenTableIds ?? [],
      }
  const positions = migrated.positions as Record<string, { x: number; y: number }>
  const migratedTables = migrated.tables as unknown as MetadataTable[]
  const migratedRels = migrated.rels as unknown as MetadataRelationship[]

  // Tables: merge positions back; fall back to table.x/y for legacy
  const tables: Table[] = migratedTables.map((t) => {
    const pos = positions[t.id] ?? { x: t.x ?? 0, y: t.y ?? 0 }
    return {
      id: t.id,
      schema: t.schema,
      name: t.name,
      fullName: t.fullName,
      comment: t.comment,
      g: t.g,
      x: pos.x,
      y: pos.y,
      width: t.width,
      cols: t.cols.map(metaToColumn),
      indexes: t.indexes ?? [],
    }
  })

  // Groups: Array → Record
  const groups: Groups = {}
  for (const g of metadata.groups ?? []) {
    groups[g.id] = g
  }

  // Relationships
  const rels: Relation[] = migratedRels.map((r) => ({
    id: r.id,
    from: r.from,
    to: r.to,
    col: r.col,
    type: r.type,
    fromCardinality: r.fromCardinality,
    toCardinality: r.toCardinality,
    mid: r.mid,
    fromAnchor: r.fromAnchor,
    toAnchor: r.toAnchor,
    fromTableName: '',
    toTableName: '',
    fromSchema: '',
    toSchema: '',
    fromColumn: r.col,
    toColumn: '',
    pts: [],
    waypoints: r.waypoints ?? [],
    isManual: r.isManual ?? false,
    labelT: r.labelT ?? 0.5,
    constraintName: r.constraintName,
    relationshipKey: r.relationshipKey,
  }))

  // Defaults
  const defaults: ColumnDefaults = {
    prefix: (metadata.settings?.defaults?.prefix ?? []).map(metaToColumn),
    suffix: (metadata.settings?.defaults?.suffix ?? []).map(metaToColumn),
  }

  const dialect = metadata.project?.dialect ?? 'postgresql'

  return {
    diagram: {
      tables,
      rels,
      groups,
      defaults,
      projectName: metadata.project?.name ?? '',
      dialect,
    },
    viewport: {
      sc: metadata.layout?.zoom ?? 1,
      tx: metadata.layout?.pan?.x ?? 0,
      ty: metadata.layout?.pan?.y ?? 0,
    },
    tabView: {
      hiddenTableIds: migrated.hiddenTableIds,
    },
    project: {
      name: metadata.project?.name ?? '',
      description: metadata.project?.description ?? '',
      dialect,
    },
  }
}

// ── migrateDiagramSnapshotV6ToMetadataV1 ──────────────────────────────────────

export function migrateDiagramSnapshotV6ToMetadataV1(
  snapshot: DiagramVersionSnapshot,
): ProjectMetadataV1 {
  const positions: Record<string, { x: number; y: number }> = {}
  const tables: MetadataTable[] = (snapshot.tables ?? []).map((t) => {
    positions[t.id] = { x: t.x, y: t.y }
    return {
      id: t.id,
      schema: t.schema,
      name: t.name,
      fullName: t.fullName,
      comment: t.comment,
      g: t.g,
      width: t.width,
      cols: t.cols.map(columnToMeta),
      indexes: t.indexes ?? [],
    }
  })

  // groups: Record<id, Group> → Array
  const groups: MetadataGroup[] = Object.values(snapshot.groups ?? {})

  const relationships: MetadataRelationship[] = (snapshot.rels ?? []).map((r) => ({
    id: r.id,
    from: r.from,
    to: r.to,
    col: r.col,
    type: r.type,
    fromCardinality: r.fromCardinality,
    toCardinality: r.toCardinality,
    mid: r.mid,
    fromAnchor: r.fromAnchor,
    toAnchor: r.toAnchor,
    waypoints: r.waypoints ?? [],
    isManual: r.isManual,
    labelT: r.labelT,
    constraintName: r.constraintName,
    relationshipKey: r.relationshipKey,
  }))

  const defaults = {
    prefix: (snapshot.defaults?.prefix ?? []).map(columnToMeta),
    suffix: (snapshot.defaults?.suffix ?? []).map(columnToMeta),
  }

  const meta = emptyMetadataV1({
    name: snapshot.projectName ?? '',
    description: '',
    dialect: snapshot.dialect ?? 'postgresql',
  })

  return {
    ...meta,
    tables,
    relationships,
    groups,
    layout: {
      positions,
      zoom: snapshot.view?.sc ?? 1,
      pan: { x: snapshot.view?.tx ?? 0, y: snapshot.view?.ty ?? 0 },
    },
    settings: {
      defaults,
      defaultLineMode: snapshot.defaultLineMode === 'curved'
        ? 'bezier'
        : snapshot.defaultLineMode ?? 'orthogonal',
      relAnimation: snapshot.relAnimation ?? true,
      grid: snapshot.grid ?? true,
      userView: {
        hiddenTableIds: snapshot.hiddenTableIds ?? [],
        sidebarCollapsed: Object.values(snapshot.sbCollapsed ?? {}).some(Boolean),
        theme: snapshot.theme ?? 'system',
      },
    },
  }
}
